/**
 * What the navigation card can be asked to show, decided from the props alone.
 *
 * Pure on purpose: these are the rules an experience's declared navigation has
 * to satisfy before anything renders, and keeping them out of the component
 * lets them be read (and tested) without a render.
 */

const isNonEmptyString = (value) => typeof value === 'string' && value.length > 0

/**
 * Somewhere to actually go. Every row the card renders leads somewhere - a
 * route to push or a callback to run - so a row without either would push
 * `undefined` onto history the moment it was tapped.
 */
export const hasNavDestination = (candidate) =>
  isNonEmptyString(candidate?.to) || typeof candidate?.onClick === 'function'

/** A row the card can render: identified, labelled, illustrated, and going somewhere. */
export const isValidNavItem = (item) =>
  isNonEmptyString(item?.id) && isNonEmptyString(item?.label) && item?.icon != null && hasNavDestination(item)

/** The level above the listed rows - same destination contract, no icon of its own. */
export const isValidNavParent = (parent) => isNonEmptyString(parent?.label) && hasNavDestination(parent)

/**
 * The two destinations either side of the row the visitor is on.
 *
 * The list is a ring: a section is a set of peers with no first or last one, so
 * stepping past either end comes back around rather than dead-ending on a
 * greyed-out arrow. Returns null where stepping means nothing - a screen deeper
 * than any row (no anchor) or a list of one (nowhere else to be).
 */
export const getNavStepRing = (items) => {
  const currentIndex = items.findIndex((item) => item.current)
  if (currentIndex < 0 || items.length < 2) return null

  return {
    previous: items[(currentIndex - 1 + items.length) % items.length],
    next: items[(currentIndex + 1) % items.length],
  }
}

/**
 * Everything the card needs about the level it is indexing, reduced from what
 * the experience declared: which rows are renderable, which level sits above
 * them, and where the visitor is among them.
 *
 * @param {object} declared
 * @param {Array} declared.navItems rows the experience passed to ExperienceShell
 * @param {object|null} declared.navParent the level above, if the experience named one
 * @param {object|null} declared.registration a mounted ExperienceNavBack, if any
 * @returns {object} `items`, `hasItems`, `hasDeclaredParent`, `parent` (the level-above row),
 *   `showBack` (whether the registration renders as its own row), `stepRing`
 */
export const buildNavigationIndex = ({ navItems, navParent, registration }) => {
  const items = navItems.filter(isValidNavItem)
  // An app-level navParent describes the whole level structure, so it beats a
  // contextual registration; a registration that named itself
  // (ExperienceNavBack's `label`) is the next best thing; anything else falls
  // through to the generic Back row, which the card renders instead.
  const hasDeclaredParent = isValidNavParent(navParent)
  const parent = hasDeclaredParent
    ? navParent
    : (registration?.label && { label: registration.label, ariaLabel: registration.ariaLabel }) || null

  // One level-above row, whichever face it wears: the declared parent or the
  // registration's own name where there is one, the generic Back only when
  // nothing names the level above (Picture Our Heritage, Record My Story). A
  // registration under a declared level folds into that level even when it
  // has a handler of its own - the level structure is the card's way up, and
  // a generic Back never sits beside it.
  const showBack = Boolean(registration) && !parent

  return { items, hasItems: items.length > 0, hasDeclaredParent, parent, showBack, stepRing: getNavStepRing(items) }
}

/** The kinds of row the card holds; also what the handle can wear when one is all there is. */
export const NAV_ROW_KINDS = {
  BACK: 'back',
  PARENT: 'parent',
  ITEM: 'item',
  HOME: 'home',
  KIDS_RETURN: 'kids-return',
  LEAVE_KIOSK: 'leave-kiosk',
}

/**
 * The card's one row, where it would hold exactly one.
 *
 * A card of one row is a menu with nothing to choose from: opening it only
 * puts a tap and a full-screen overlay between the visitor and the one place
 * they can go. So the handle becomes that row instead - its glyph, its name,
 * its action. Returns null wherever there is a real choice to make, and where
 * the only row is the page the visitor is already on (it would go nowhere).
 *
 * Takes the same flags the card renders from, so the two can never disagree
 * about what the card would have shown.
 */
export const getSoleNavRow = ({ parentItem, showBack, navItems, showHome, showKidsReturn, showLeaveKiosk }) => {
  const rows = [
    showBack && { kind: NAV_ROW_KINDS.BACK },
    parentItem && { kind: NAV_ROW_KINDS.PARENT, item: parentItem },
    ...navItems.map((item) => ({ kind: NAV_ROW_KINDS.ITEM, item })),
    showHome && { kind: NAV_ROW_KINDS.HOME },
    showKidsReturn && { kind: NAV_ROW_KINDS.KIDS_RETURN },
    showLeaveKiosk && { kind: NAV_ROW_KINDS.LEAVE_KIOSK },
  ].filter(Boolean)

  if (rows.length !== 1) return null

  const [sole] = rows
  return sole.item?.current ? null : sole
}
